// SNIGJ - Case Requisition Page - Hierarchical information flow with progress tracking
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Send, Inbox, ArrowDown, CheckCircle, XCircle, Clock, 
  AlertTriangle, FileText, User, Scale, ArrowRightLeft,
  ChevronRight, Users, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RequisitionPage = () => {
  const { user, language } = useAuth();
  const [sentRequisitions, setSentRequisitions] = useState([]);
  const [receivedRequisitions, setReceivedRequisitions] = useState([]);
  const [subordinates, setSubordinates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [cases, setCases] = useState([]);
  const [userLevel, setUserLevel] = useState(0);
  
  // New requisition form
  const [newRequisition, setNewRequisition] = useState({
    target_user_id: '',
    case_id: '',
    reason: ''
  });
  
  // Response form
  const [responseForm, setResponseForm] = useState({
    action: 'fulfill',
    response: ''
  });

  useEffect(() => {
    loadRequisitions();
    loadSubordinates();
    loadCases();
  }, []);

  const loadRequisitions = async () => {
    try {
      setLoading(true);
      const [sentRes, receivedRes] = await Promise.all([
        api.get('/requisitions/sent'),
        api.get('/requisitions/received')
      ]);
      setSentRequisitions(sentRes.data.requisitions || []);
      setReceivedRequisitions(receivedRes.data.requisitions || []);
    } catch (error) {
      console.error('Failed to load requisitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubordinates = async () => {
    try {
      const response = await api.get('/admin/subordinates');
      setSubordinates(response.data.subordinates || []);
      setUserLevel(response.data.your_level || 0);
    } catch (error) {
      console.error('Failed to load subordinates:', error);
    }
  };

  const loadCases = async () => {
    try {
      const response = await api.get('/cases');
      setCases(response.data.cases || []);
    } catch (error) {
      console.error('Failed to load cases:', error);
    }
  };

  const handleCreateRequisition = async () => {
    try {
      if (!newRequisition.target_user_id || !newRequisition.case_id || !newRequisition.reason) {
        toast.error(language === 'fr' ? 'Veuillez remplir tous les champs' : 'Please fill all fields');
        return;
      }
      
      await api.post('/requisitions/create', newRequisition);
      toast.success(language === 'fr' ? 'Réquisition envoyée' : 'Requisition sent');
      setShowCreateModal(false);
      setNewRequisition({ target_user_id: '', case_id: '', reason: '' });
      loadRequisitions();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error';
      toast.error(detail);
    }
  };

  const handleRespondToRequisition = async () => {
    try {
      if (!selectedRequisition) return;
      
      await api.put(`/requisitions/${selectedRequisition.requisition_id}/respond`, {
        action: responseForm.action,
        response: responseForm.response
      });
      
      toast.success(language === 'fr' 
        ? (responseForm.action === 'fulfill' ? 'Réquisition traitée' : 'Réquisition rejetée')
        : (responseForm.action === 'fulfill' ? 'Requisition fulfilled' : 'Requisition rejected')
      );
      
      setShowResponseModal(false);
      setSelectedRequisition(null);
      setResponseForm({ action: 'fulfill', response: '' });
      loadRequisitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openResponseModal = (requisition) => {
    setSelectedRequisition(requisition);
    setResponseForm({ action: 'fulfill', response: '' });
    setShowResponseModal(true);
  };

  // Get progress percentage based on status and levels
  const getProgressInfo = (requisition) => {
    const requesterLevel = requisition.requester_level || 0;
    const targetLevel = requisition.target_level || 0;
    const levelDiff = requesterLevel - targetLevel;
    
    let progress = 0;
    let statusText = '';
    let color = 'bg-yellow-500';
    
    switch (requisition.status) {
      case 'PENDING':
        progress = 50;
        statusText = language === 'fr' ? 'En attente de réponse' : 'Awaiting response';
        color = 'bg-yellow-500';
        break;
      case 'FULFILLED':
        progress = 100;
        statusText = language === 'fr' ? 'Réquisition traitée' : 'Requisition fulfilled';
        color = 'bg-green-500';
        break;
      case 'REJECTED':
        progress = 100;
        statusText = language === 'fr' ? 'Réquisition rejetée' : 'Requisition rejected';
        color = 'bg-red-500';
        break;
      default:
        progress = 0;
        statusText = language === 'fr' ? 'Nouveau' : 'New';
    }
    
    return { progress, statusText, color, levelDiff };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="outline" className="gap-1 bg-yellow-500/10 text-yellow-600 border-yellow-500"><Clock className="w-3 h-3" />{language === 'fr' ? 'En attente' : 'Pending'}</Badge>;
      case 'FULFILLED':
        return <Badge className="gap-1 bg-green-500 text-white"><CheckCircle className="w-3 h-3" />{language === 'fr' ? 'Traitée' : 'Fulfilled'}</Badge>;
      case 'REJECTED':
        return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" />{language === 'fr' ? 'Rejetée' : 'Rejected'}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Group subordinates by level
  const subordinatesByLevel = subordinates.reduce((acc, sub) => {
    const level = sub.hierarchy_level || 0;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(sub);
    return acc;
  }, {});

  const pendingReceived = receivedRequisitions.filter(r => r.status === 'PENDING').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="requisition-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Scale className="w-8 h-8 text-primary" />
              {language === 'fr' ? "Réquisitions d'Information" : 'Information Requisitions'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Les demandes descendent la hiérarchie - du supérieur vers le subalterne'
                : 'Requests flow down the hierarchy - from superior to subordinate'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right text-sm">
              <p className="font-medium">{language === 'fr' ? 'Votre niveau' : 'Your level'}: <span className="text-primary font-bold">{userLevel}</span></p>
              <p className="text-muted-foreground">{subordinates.length} {language === 'fr' ? 'subalterne(s)' : 'subordinate(s)'}</p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="gap-2" data-testid="new-requisition-btn">
              <Send className="w-4 h-4" />
              {language === 'fr' ? 'Nouvelle Réquisition' : 'New Requisition'}
            </Button>
          </div>
        </div>

        {/* Hierarchy Info Card */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">
                  {language === 'fr' ? 'Principe de hiérarchie' : 'Hierarchy Principle'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? "Pour éviter les abus de pouvoir, les réquisitions d'information sur les dossiers doivent être émises par un supérieur hiérarchique vers un subalterne. Un procureur général ne peut pas réquisitionner d'informations directement auprès d'un simple agent, il doit passer par la chaîne de commandement."
                    : "To prevent abuse of power, information requisitions must be issued from a hierarchical superior to a subordinate."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="received" className="space-y-4">
          <TabsList>
            <TabsTrigger value="received" className="gap-2" data-testid="tab-received">
              <Inbox className="w-4 h-4" />
              {language === 'fr' ? 'Reçues' : 'Received'}
              {pendingReceived > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full">
                  {pendingReceived}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="gap-2" data-testid="tab-sent">
              <Send className="w-4 h-4" />
              {language === 'fr' ? 'Envoyées' : 'Sent'}
            </TabsTrigger>
          </TabsList>

          {/* Received Tab */}
          <TabsContent value="received">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Réquisitions Reçues' : 'Received Requisitions'}</CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Demandes d\'information de vos supérieurs hiérarchiques'
                    : 'Information requests from your hierarchical superiors'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-24 skeleton rounded" />)}
                  </div>
                ) : receivedRequisitions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Inbox className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune réquisition reçue' : 'No requisitions received'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {receivedRequisitions.map((req) => {
                      const progressInfo = getProgressInfo(req);
                      return (
                        <div 
                          key={req.requisition_id} 
                          className={`p-4 rounded-lg border ${req.status === 'PENDING' ? 'border-yellow-500 bg-yellow-500/5' : 'border-border'}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <ArrowDown className="w-4 h-4 text-blue-500" />
                                <span className="font-medium">
                                  {language === 'fr' ? 'De:' : 'From:'} {t(language, `roles.${req.requester_role}`) || req.requester_role}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  Niveau {req.requester_level}
                                </Badge>
                                {getStatusBadge(req.status)}
                              </div>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {language === 'fr' ? 'Dossier:' : 'Case:'} {req.case_id}
                              </p>
                              <p className="text-sm mt-2 bg-muted/50 p-2 rounded">{req.reason}</p>
                              {req.response && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded border border-green-500/20">
                                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                                    {language === 'fr' ? 'Votre réponse:' : 'Your response:'}
                                  </p>
                                  <p className="text-sm">{req.response}</p>
                                </div>
                              )}
                            </div>
                            {req.status === 'PENDING' && (
                              <Button 
                                size="sm" 
                                onClick={() => openResponseModal(req)}
                                data-testid={`respond-btn-${req.requisition_id}`}
                              >
                                {language === 'fr' ? 'Répondre' : 'Respond'}
                              </Button>
                            )}
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-3 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{progressInfo.statusText}</span>
                              <span className="font-medium">{progressInfo.progress}%</span>
                            </div>
                            <Progress value={progressInfo.progress} className={`h-2 ${progressInfo.color}`} />
                          </div>
                          
                          <div className="mt-2 text-xs text-muted-foreground">
                            {new Date(req.created_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sent Tab */}
          <TabsContent value="sent">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Réquisitions Envoyées' : 'Sent Requisitions'}</CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Vos demandes d\'information aux subalternes'
                    : 'Your information requests to subordinates'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-24 skeleton rounded" />)}
                  </div>
                ) : sentRequisitions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Send className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune réquisition envoyée' : 'No requisitions sent'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sentRequisitions.map((req) => {
                      const progressInfo = getProgressInfo(req);
                      return (
                        <div key={req.requisition_id} className="p-4 rounded-lg border">
                          <div className="flex items-start justify-between mb-3">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <ArrowDown className="w-4 h-4 text-green-500" />
                                <span className="font-medium">
                                  {language === 'fr' ? 'À:' : 'To:'} {t(language, `roles.${req.target_role}`) || req.target_role}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  Niveau {req.target_level}
                                </Badge>
                                {getStatusBadge(req.status)}
                              </div>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {language === 'fr' ? 'Dossier:' : 'Case:'} {req.case_id}
                              </p>
                              <p className="text-sm mt-2 bg-muted/50 p-2 rounded">{req.reason}</p>
                              {req.response && (
                                <div className="mt-2 p-2 bg-blue-500/10 rounded border border-blue-500/20">
                                  <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                    {language === 'fr' ? 'Réponse reçue:' : 'Response received:'}
                                  </p>
                                  <p className="text-sm">{req.response}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-3 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{progressInfo.statusText}</span>
                              <span className="font-medium">{progressInfo.progress}%</span>
                            </div>
                            <Progress value={progressInfo.progress} className={`h-2`} />
                          </div>
                          
                          <div className="mt-2 text-xs text-muted-foreground">
                            {new Date(req.created_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Requisition Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5" />
                {language === 'fr' ? 'Nouvelle Réquisition' : 'New Requisition'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? 'Envoyez une demande d\'information à un subalterne hiérarchique'
                  : 'Send an information request to a hierarchical subordinate'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Subordinates grouped by level */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {language === 'fr' ? 'Destinataire (subalterne)' : 'Recipient (subordinate)'}
                </Label>
                <Select 
                  value={newRequisition.target_user_id} 
                  onValueChange={(v) => setNewRequisition({...newRequisition, target_user_id: v})}
                >
                  <SelectTrigger data-testid="subordinate-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner un subalterne' : 'Select a subordinate'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {subordinates.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground text-center">
                        {language === 'fr' ? 'Aucun subalterne disponible' : 'No subordinates available'}
                      </div>
                    ) : (
                      Object.entries(subordinatesByLevel)
                        .sort(([a], [b]) => Number(b) - Number(a))
                        .map(([level, subs]) => (
                          <React.Fragment key={level}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                              {language === 'fr' ? `Niveau ${level}` : `Level ${level}`} 
                              <span className="ml-1 text-primary">({userLevel - Number(level)} {language === 'fr' ? 'niveaux en dessous' : 'levels below'})</span>
                            </div>
                            {subs.map(sub => (
                              <SelectItem key={sub.user_id} value={sub.user_id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                                    {sub.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <span className="font-medium">{sub.name}</span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                      ({t(language, `roles.${sub.role}`) || sub.role})
                                    </span>
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {language === 'fr' 
                    ? `Seuls les utilisateurs de niveau inférieur à ${userLevel} sont affichés`
                    : `Only users below level ${userLevel} are shown`}
                </p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {language === 'fr' ? 'Dossier concerné' : 'Related Case'}
                </Label>
                <Select 
                  value={newRequisition.case_id} 
                  onValueChange={(v) => setNewRequisition({...newRequisition, case_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner un dossier' : 'Select a case'} /></SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {cases.map(c => (
                      <SelectItem key={c.case_id} value={c.case_id}>
                        {c.case_number || c.case_id} - {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Motif de la réquisition' : 'Reason for Requisition'}</Label>
                <Textarea 
                  value={newRequisition.reason}
                  onChange={(e) => setNewRequisition({...newRequisition, reason: e.target.value})}
                  placeholder={language === 'fr' 
                    ? 'Expliquez pourquoi vous avez besoin de ces informations...'
                    : 'Explain why you need this information...'}
                  rows={3}
                />
              </div>

              {/* Preview of hierarchy flow */}
              {newRequisition.target_user_id && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium mb-2">{language === 'fr' ? 'Flux hiérarchique:' : 'Hierarchy flow:'}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className="bg-primary/10">
                      {language === 'fr' ? 'Vous' : 'You'} (Niv. {userLevel})
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    <ArrowDown className="w-4 h-4 text-green-500" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    <Badge variant="outline">
                      {subordinates.find(s => s.user_id === newRequisition.target_user_id)?.name || '?'} 
                      (Niv. {subordinates.find(s => s.user_id === newRequisition.target_user_id)?.hierarchy_level || '?'})
                    </Badge>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleCreateRequisition} className="gap-2" disabled={!newRequisition.target_user_id}>
                <Send className="w-4 h-4" />
                {language === 'fr' ? 'Envoyer' : 'Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Response Modal */}
        <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {language === 'fr' ? 'Répondre à la Réquisition' : 'Respond to Requisition'}
              </DialogTitle>
              <DialogDescription>
                {selectedRequisition && (
                  <span>
                    {language === 'fr' ? 'Dossier:' : 'Case:'} {selectedRequisition.case_id}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Action' : 'Action'}</Label>
                <div className="flex gap-2">
                  <Button 
                    variant={responseForm.action === 'fulfill' ? 'default' : 'outline'}
                    className="flex-1 gap-2"
                    onClick={() => setResponseForm({...responseForm, action: 'fulfill'})}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {language === 'fr' ? 'Traiter' : 'Fulfill'}
                  </Button>
                  <Button 
                    variant={responseForm.action === 'reject' ? 'destructive' : 'outline'}
                    className="flex-1 gap-2"
                    onClick={() => setResponseForm({...responseForm, action: 'reject'})}
                  >
                    <XCircle className="w-4 h-4" />
                    {language === 'fr' ? 'Rejeter' : 'Reject'}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Réponse' : 'Response'}</Label>
                <Textarea 
                  value={responseForm.response}
                  onChange={(e) => setResponseForm({...responseForm, response: e.target.value})}
                  placeholder={language === 'fr' 
                    ? 'Votre réponse ou les informations demandées...'
                    : 'Your response or the requested information...'}
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResponseModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={handleRespondToRequisition} className="gap-2">
                {language === 'fr' ? 'Envoyer la réponse' : 'Send Response'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default RequisitionPage;
